import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { feature } from "topojson-client";
import type { FeatureCollection, Geometry } from "geojson";
import type { Topology } from "topojson-specification";
import type { Country, Region } from "../lib/types";
import { getRegionColor, REGION_COLOR_DEFAULT } from "../lib/regionColors";

// Bundled topojson with iso_a2 in every feature's properties.
// Imported statically at bundle time — NO runtime CDN fetch.
import topoData from "../assets/countries-110m.topo.json";

interface WorldMapProps {
  entitiesByCountry: Record<string, number>;
  /** Fetched country rows — used to derive region coloring dynamically. */
  countries: Country[];
  height?: number;
  onCountryClick?: (countryName: string) => void;
  /** When set, non-matching regions dim to grey at 0.3 opacity. */
  selectedRegion?: Region | null;
}

type CountryFeature = GeoJSON.Feature<Geometry, { name: string; iso_a2: string }>;

/**
 * Parse the bundled topology once at module level.
 * _allFeatures and _featureIndex are both derived from this single call to
 * feature(), so the topology is never expanded more than once per page load.
 */
function buildModuleLevelData(): {
  allFeatures: CountryFeature[];
  featureIndex: Map<string, CountryFeature>;
  fc: FeatureCollection<Geometry, { name: string; iso_a2: string }>;
} {
  const featureIndex = new Map<string, CountryFeature>();
  const topology = topoData as unknown as Topology;
  const objects = topology.objects?.countries;
  if (!objects) {
    return { allFeatures: [], featureIndex, fc: { type: "FeatureCollection", features: [] } };
  }
  const fc = feature(topology, objects) as unknown as FeatureCollection<
    Geometry,
    { name: string; iso_a2: string }
  >;
  const allFeatures = fc.features as CountryFeature[];
  for (const f of allFeatures) {
    const iso = f.properties?.iso_a2?.toUpperCase();
    if (iso) featureIndex.set(iso, f);
  }
  return { allFeatures, featureIndex, fc };
}

// Module-level constants — built once on import, stable across all renders.
const { allFeatures: _allFeatures, featureIndex: _featureIndex, fc: _fc } = buildModuleLevelData();


export function WorldMap({
  entitiesByCountry,
  countries,
  height,
  onCountryClick,
  selectedRegion,
}: WorldMapProps): JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const isMobile =
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 639px)").matches;
  const resolvedHeight = height ?? (isMobile ? 280 : 460);

  /**
   * iso2RegionMap: Map<iso2_uppercase, Region>
   * Built from countries prop keyed on iso2 (not name).
   * When two rows share iso2 (Seychelles SC), the last writer wins; both rows
   * currently share the same region so this is safe. In the unlikely event of
   * a mismatch, the row with the most entities is preferred — achieved by
   * sorting countries by _count DESC before building.
   */
  const iso2RegionMap = useMemo<Map<string, Region>>(() => {
    const m = new Map<string, Region>();
    // Sort by entity count DESC so the larger row's region wins on collision.
    const sorted = [...countries].sort(
      (a, b) => (b._count?.entities ?? 0) - (a._count?.entities ?? 0),
    );
    for (const c of sorted) {
      m.set(c.iso2.toUpperCase(), c.region);
    }
    return m;
  }, [countries]);

  /**
   * entitiesByIso2: aggregate entity counts across all Country rows sharing
   * the same iso2 (handles Seychelles SC having two DB rows).
   */
  const entitiesByIso2 = useMemo<Record<string, number>>(() => {
    const m: Record<string, number> = {};
    for (const c of countries) {
      const iso = c.iso2.toUpperCase();
      m[iso] = (m[iso] ?? 0) + (entitiesByCountry[c.name] ?? 0);
    }
    return m;
  }, [countries, entitiesByCountry]);

  useEffect(() => {
    if (!svgRef.current) return;

    try {
      if (_allFeatures.length === 0) throw new Error("Topology missing 'countries' object");

      const fc = _fc;
      const features = _allFeatures;

      const width = 960;
      const innerHeight = 500;
      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const projection = d3.geoNaturalEarth1().fitSize([width, innerHeight], fc);
      const path = d3.geoPath(projection);

      // ── Country fills ──────────────────────────────────────────────────────
      const sel = svg
        .selectAll("path.country")
        .data(features)
        .enter()
        .append("path")
        .attr("class", "country")
        .attr("d", (d) => path(d) ?? "")
        .attr("fill", (d) => {
          const iso = d.properties.iso_a2?.toUpperCase();
          const region = iso ? iso2RegionMap.get(iso) : undefined;
          if (selectedRegion && region !== selectedRegion) return REGION_COLOR_DEFAULT.hex;
          return getRegionColor(region, "hex");
        })
        .attr("opacity", (d) => {
          const iso = d.properties.iso_a2?.toUpperCase();
          const region = iso ? iso2RegionMap.get(iso) : undefined;
          return selectedRegion && region !== selectedRegion ? 0.3 : 1;
        })
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 0.5)
        .style("cursor", onCountryClick ? "pointer" : "default");

      // ── Tooltips (iso2-based count lookup, aggregated for shared iso2) ─────
      sel.append("title").text((d) => {
        const iso = d.properties.iso_a2?.toUpperCase() ?? "";
        const n = d.properties.name;
        const c = entitiesByIso2[iso] ?? 0;
        return c > 0 ? `${n} · ${c} entit${c === 1 ? "y" : "ies"}` : n;
      });

      // ── Country click — resolve DB country name by iso2 ───────────────────
      if (onCountryClick) {
        sel.on("click", (_e, d) => {
          const iso = d.properties.iso_a2?.toUpperCase() ?? "";
          // Find the first DB country whose iso2 matches (handles multi-row iso2).
          const dbCountry = countries.find((c) => c.iso2.toUpperCase() === iso);
          onCountryClick(dbCountry?.name ?? d.properties.name);
        });
      }

      // ── Entity markers ─────────────────────────────────────────────────────
      // Rendered for ALL countries that have either a feature centroid or
      // non-zero stored lat/lng. Each iso2 gets at most one marker (aggregated).

      // Build the set of unique iso2 values that should have a marker,
      // along with the "representative" country for region colour.
      // For Seychelles (two rows sharing SC), use the one with the most entities.
      const iso2Seen = new Set<string>();
      const markerEntries: Array<{
        iso2: string;
        country: Country;
        region: Region;
      }> = [];

      const sortedByCount = [...countries].sort(
        (a, b) =>
          (entitiesByIso2[b.iso2.toUpperCase()] ?? 0) -
          (entitiesByIso2[a.iso2.toUpperCase()] ?? 0),
      );

      for (const c of sortedByCount) {
        const iso = c.iso2.toUpperCase();
        if (iso2Seen.has(iso)) continue;
        iso2Seen.add(iso);
        markerEntries.push({ iso2: iso, country: c, region: c.region });
      }

      // Resolve SVG [x, y] for each marker entry.
      interface MarkerDatum {
        cx: number;
        cy: number;
        region: Region;
        iso2: string;
        name: string;
      }

      const markerData: MarkerDatum[] = [];
      for (const entry of markerEntries) {
        const f = _featureIndex.get(entry.iso2);
        let cx: number | null = null;
        let cy: number | null = null;

        if (f) {
          const centroid = path.centroid(f);
          if (centroid && Number.isFinite(centroid[0]) && Number.isFinite(centroid[1])) {
            cx = centroid[0];
            cy = centroid[1];
          }
        }

        // Stored lat/lng override — only when non-zero (operator-supplied).
        if (
          cx === null &&
          entry.country.lat !== 0 &&
          entry.country.lng !== 0 &&
          Number.isFinite(entry.country.lat) &&
          Number.isFinite(entry.country.lng)
        ) {
          const projected = projection([entry.country.lng, entry.country.lat]);
          if (projected) {
            cx = projected[0];
            cy = projected[1];
          }
        }

        if (cx !== null && cy !== null) {
          markerData.push({
            cx,
            cy,
            region: entry.region,
            iso2: entry.iso2,
            name: entry.country.name,
          });
        } else {
          // Log countries with no feature AND no stored coords so operators notice.
          // eslint-disable-next-line no-console
          console.debug(
            "[WorldMap] no topojson feature for iso2:",
            entry.country.iso2,
            "name:",
            entry.country.name,
          );
        }
      }

      svg
        .append("g")
        .attr("class", "markers")
        .selectAll("circle")
        .data(markerData)
        .enter()
        .append("circle")
        .attr("cx", (d) => d.cx)
        .attr("cy", (d) => d.cy)
        .attr("r", 4)
        .attr("fill", (d) => getRegionColor(d.region, "hex"))
        .attr("stroke", "#ffffff")
        .attr("stroke-width", 1.5)
        .attr("opacity", (d) =>
          selectedRegion && d.region !== selectedRegion ? 0.25 : 1,
        )
        .style("pointer-events", "auto")
        .style("cursor", onCountryClick ? "pointer" : "default")
        .on("click", (_e: MouseEvent, d) => {
          if (onCountryClick) onCountryClick(d.name);
        });

      setLoaded(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [entitiesByCountry, onCountryClick, iso2RegionMap, countries, selectedRegion, entitiesByIso2]);

  return (
    <div className="relative">
      {!loaded && !error && (
        <div
          className="skeleton w-full"
          style={{ height: resolvedHeight, borderRadius: 12 }}
          aria-hidden
        />
      )}
      {error && (
        <div className="text-xs text-text-muted p-4">
          World map failed to load: {error}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox="0 0 960 500"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Interactive world map of Hightekers regions"
        style={{
          width: "100%",
          height: resolvedHeight,
          background: "var(--bg)",
          borderRadius: 12,
          display: loaded ? "block" : "none",
        }}
      />
    </div>
  );
}
